/**
 * layout/backgrounds.js - Section Background Definitions
 * Location: /app/frontend/src/admin/page_builder/layout/
 * 
 * Defines section-level background options:
 * - Solid colors (from tokens or custom)
 * - Gradients (linear, radial)
 * - Images (with positioning)
 * - Overlay opacity for visual emphasis
 */

/**
 * Background type constants
 */
export const BackgroundType = {
  NONE: 'none',
  SOLID: 'solid',
  GRADIENT: 'gradient',
  IMAGE: 'image',
};

/**
 * Gradient direction presets
 */
export const GradientDirection = {
  TO_BOTTOM: 'to bottom',
  TO_TOP: 'to top',
  TO_RIGHT: 'to right',
  TO_LEFT: 'to left',
  TO_BOTTOM_RIGHT: 'to bottom right',
  TO_BOTTOM_LEFT: 'to bottom left',
  TO_TOP_RIGHT: 'to top right',
  TO_TOP_LEFT: 'to top left',
  RADIAL: 'radial',
};

/**
 * Image position presets
 */
export const ImagePosition = {
  CENTER: 'center center',
  TOP: 'center top',
  BOTTOM: 'center bottom',
  LEFT: 'left center',
  RIGHT: 'right center',
  TOP_LEFT: 'left top',
  TOP_RIGHT: 'right top',
  BOTTOM_LEFT: 'left bottom',
  BOTTOM_RIGHT: 'right bottom',
};

/**
 * Image size presets
 */
export const ImageSize = {
  COVER: 'cover',
  CONTAIN: 'contain',
  AUTO: 'auto',
};

/**
 * @typedef {Object} SolidBackground
 * @property {'solid'} type
 * @property {string} color - Hex color or token reference
 */

/**
 * @typedef {Object} GradientBackground
 * @property {'gradient'} type
 * @property {string} direction - Gradient direction
 * @property {string} from - Start color (hex or token)
 * @property {string} to - End color (hex or token)
 * @property {string} [via] - Optional middle color
 */

/**
 * @typedef {Object} ImageBackground
 * @property {'image'} type
 * @property {string} url - Image URL
 * @property {string} [position] - Background position
 * @property {string} [size] - Background size
 * @property {boolean} [fixed] - Fixed attachment
 */

/**
 * @typedef {Object} SectionBackground
 * @property {BackgroundType} type
 * @property {SolidBackground|GradientBackground|ImageBackground} [config]
 * @property {number} [overlayOpacity] - 0-100, overlay darkness
 * @property {string} [overlayColor] - Overlay color (default: black)
 */

/**
 * Generate CSS for a solid background
 * @param {SolidBackground} config
 * @returns {Object} CSS style object
 */
function getSolidBackgroundStyles(config) {
  return {
    backgroundColor: config.color || 'transparent',
  };
}

/**
 * Generate CSS for a gradient background
 * @param {GradientBackground} config
 * @returns {Object} CSS style object
 */
function getGradientBackgroundStyles(config) {
  const { direction, from, to, via } = config;
  
  if (direction === 'radial') {
    const gradient = via 
      ? `radial-gradient(circle, ${from}, ${via}, ${to})`
      : `radial-gradient(circle, ${from}, ${to})`;
    return { backgroundImage: gradient };
  }
  
  const gradient = via
    ? `linear-gradient(${direction}, ${from}, ${via}, ${to})`
    : `linear-gradient(${direction}, ${from}, ${to})`;
  
  return { backgroundImage: gradient };
}

/**
 * Generate CSS for an image background
 * @param {ImageBackground} config
 * @returns {Object} CSS style object
 */
function getImageBackgroundStyles(config) {
  return {
    backgroundImage: `url(${config.url})`,
    backgroundPosition: config.position || ImagePosition.CENTER,
    backgroundSize: config.size || ImageSize.COVER,
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: config.fixed ? 'fixed' : 'scroll',
  };
}

/**
 * Get CSS styles for a section background
 * @param {SectionBackground} background
 * @returns {Object} CSS style object for the background
 */
export function getBackgroundStyles(background) {
  if (!background || background.type === BackgroundType.NONE) {
    return {};
  }
  
  let styles = {};
  
  switch (background.type) {
    case BackgroundType.SOLID:
      styles = getSolidBackgroundStyles(background.config || {});
      break;
    case BackgroundType.GRADIENT:
      styles = getGradientBackgroundStyles(background.config || {});
      break;
    case BackgroundType.IMAGE:
      styles = getImageBackgroundStyles(background.config || {});
      break;
    default:
      styles = {};
  }
  
  return styles;
}

/**
 * Get CSS for the overlay element
 * @param {SectionBackground} background
 * @returns {Object|null} CSS style object for overlay, or null if no overlay
 */
export function getOverlayStyles(background) {
  if (!background || typeof background.overlayOpacity !== 'number' || background.overlayOpacity <= 0) {
    return null;
  }
  
  const opacity = Math.min(100, Math.max(0, background.overlayOpacity)) / 100;
  const color = background.overlayColor || '#000000';
  
  return {
    position: 'absolute',
    inset: 0,
    backgroundColor: color,
    opacity,
    pointerEvents: 'none',
  };
}

/**
 * Default background configuration
 */
export const DEFAULT_BACKGROUND = {
  type: BackgroundType.NONE,
  config: null,
  overlayOpacity: 0,
  overlayColor: '#000000',
};

/**
 * Preset background options for quick selection
 */
export const BACKGROUND_PRESETS = [
  { name: 'None', value: { type: BackgroundType.NONE } },
  { name: 'Black', value: { type: BackgroundType.SOLID, config: { color: '#000000' } } },
  { name: 'Dark Surface', value: { type: BackgroundType.SOLID, config: { color: 'var(--tc-colors-surface, #18181b)' } } },
  { name: 'Dark Gradient', value: { 
    type: BackgroundType.GRADIENT, 
    config: { direction: GradientDirection.TO_BOTTOM, from: '#18181b', to: '#000000' } 
  }},
  { name: 'Primary Gradient', value: { 
    type: BackgroundType.GRADIENT, 
    config: { direction: GradientDirection.TO_BOTTOM_RIGHT, from: '#f97316', to: '#ea580c' } 
  }},
  { name: 'Hero Glow', value: { 
    type: BackgroundType.GRADIENT, 
    config: { direction: 'radial', from: 'rgba(249, 115, 22, 0.2)', to: 'transparent' } 
  }},
];

export default {
  BackgroundType,
  GradientDirection,
  ImagePosition,
  ImageSize,
  getBackgroundStyles,
  getOverlayStyles,
  DEFAULT_BACKGROUND,
  BACKGROUND_PRESETS,
};
